import {FilterCollapse} from 'plugins/modules/filter/components/FilterCollapse';
import {FilterCollapseSecondLevel} from 'plugins/modules/filter/components/FilterCollapseSecondLevel';
import {FilterFactory} from 'plugins/modules/filter/components/FilterFactory';
import {SearchGroupCheckboxController} from 'plugins/modules/filter/components/SearchGroupCheckboxController';
import {FilterContext} from 'plugins/modules/filter/context/new';
import React, {FC, useContext, useEffect, useRef, useState} from 'react';
import style from './style.module.scss';
import {ExpressOrder} from 'plugins/modules/filter/components/ExpressOrder';
import {FilterPickupTodayButtonContext} from 'plugins/modules/filter/context/FilterPickupTodayButtonContext';
import {useStickyDesktopFilters} from 'plugins/modules/filter/hooks/useStickyDesktopFilters';
import {IFilterFacet, IFilterFacetGroup} from 'plugins/modules/filter/types';
import {
    getItemKey,
    hasValidEnums,
    hasValidVariants,
    isAloneCheckbox,
    isValidRangeFilter,
} from 'plugins/modules/filter/helpers';
import {FILTERS_PLACEHOLDER_CONSTANTS} from 'plugins/modules/filter/components/Filters/constants';
import {SectionsFilter} from 'plugins/modules/filter/components/SectionsFilter';
import {useIsNeedRoute} from 'core/hooks/useIsNeedRoute';
import {APP_ROUTES_TO_URL} from 'core/modules/routing/constants';
import {RANGE_TYPES} from 'plugins/modules/filter/constants';

const isValidFilter = (filter: IFilterFacet): boolean => {
    const {type, variants, enum: enums} = filter;

    // проверка валидности variants
    if (variants && !hasValidVariants(variants)) {
        return false;
    }

    // проверка валидности enum
    if (enums && !hasValidEnums(enums)) {
        return false;
    }

    if (RANGE_TYPES.includes(type) && !isValidRangeFilter(filter)) {
        return false;
    }

    // проверка range фильтров
    return true;
};

export const Filters: FC = () => {
    const {filterList = [], isLoading, isFiltersLocked} = useContext(FilterContext);
    const {isShowExpressOrder} = useContext(FilterPickupTodayButtonContext);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const [isClient, setIsClient] = useState(false);
    useEffect(() => {
        setIsClient(true);
    }, []);

    const containerRef = useRef<HTMLDivElement>(null);
    const filterRef = useRef<HTMLDivElement>(null);

    useStickyDesktopFilters({containerRef, filterRef});

    // рендер одного фильтра (внутри группы)
    const renderFilterItem = (filter: IFilterFacet, groupKey: string) => {
        if (!isValidFilter(filter)) {
            return null;
        }

        const {hint, isCollapsed, search, title} = filter;
        const key = getItemKey(groupKey, filter);

        // если нет заголовка или это одиночный чекбокс — рендерим напрямую
        if (!title || isAloneCheckbox(filter)) {
            return <FilterFactory key={key} filter={filter} withSearch={search} />;
        }

        // иначе рендерим с вложенным коллапсом
        return (
            <FilterCollapseSecondLevel key={key} title={title} hint={hint} isDefaultOpen={!isCollapsed}>
                <FilterFactory filter={filter} withSearch={search} />
            </FilterCollapseSecondLevel>
        );
    };

    // рендер плоского списка фильтров (без группы)

    const renderFlatFilters = (items: IFilterFacet[], groupKey: string) =>
        items.map((filter) => {
            if (!isValidFilter(filter)) {
                return null;
            }

            const {hint, isCollapsed, search, title} = filter;
            const key = getItemKey(groupKey, filter);

            // одиночный чекбокс без обёртки
            if (isAloneCheckbox(filter)) {
                return <FilterFactory key={key} filter={filter} withSearch={search} />;
            }

            // обычный фильтр с коллапсом
            return (
                <FilterCollapse key={key} title={title} hint={hint} isDefaultOpen={!isCollapsed}>
                    <FilterFactory filter={filter} withSearch={search} />
                </FilterCollapse>
            );
        });

    // рендер группы фильтров

    const renderFilterGroup = (group: IFilterFacetGroup) => {
        const {group: groupId, groupExpand, groupHint, groupName, groupSearch, items, searchPlaceholder} = group;

        // определяем, является ли это группой
        const isGroup = 'none' !== groupName || 1 < items.length;

        // заголовок группы
        const groupTitle = 'none' !== groupName ? groupName : (items[0]?.title ?? '');

        const groupKey = `${groupId}-${groupName}`;

        // если это не группа — просто рендерим список
        if (!isGroup) {
            return renderFlatFilters(items, groupKey);
        }

        return (
            <FilterCollapse key={groupKey} title={groupTitle} hint={groupHint || ''} isDefaultOpen={groupExpand}>
                {/* поиск внутри группы */}
                {groupSearch && (
                    <SearchGroupCheckboxController
                        key={`${groupKey}-search`}
                        filterList={items}
                        searchPlaceholder={searchPlaceholder || groupTitle}
                    />
                )}

                {/* список фильтров группы */}
                {items.map((item) => renderFilterItem(item, groupKey))}
            </FilterCollapse>
        );
    };

    // проверка, является ли страница поиском
    const isSearchPage = useIsNeedRoute(APP_ROUTES_TO_URL.search);

    // данные для рендера (плейсхолдеры или реальные фильтры)
    // плейсхолдеры рендерим только на клиенте, чтобы боты не видели неактуальные фильтры
    const data = isClient && !filterList.length && isLoading ? FILTERS_PLACEHOLDER_CONSTANTS : filterList;

    return (
        <div className={style.main} ref={containerRef}>
            <div ref={filterRef}>
                {isShowExpressOrder && <ExpressOrder key={'express-order'} />}
                <div className={isFiltersLocked ? style.locked : ''}>
                    {isSearchPage && <SectionsFilter />}
                    {data.map(renderFilterGroup)}
                </div>
            </div>
        </div>
    );
};
